import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TablePagination,
  TableSearchEvent,
  TableSort,
  TableDateRangeEvent,
} from "../../../../Common/Components/Table/Type/TableType";
import { AuditWithSession } from "../../Dto/AuditModel";
import { AuditEffects } from "../../State/AuditEffects";
import { AuditState } from "../../State/AuditState";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";

@Component({
  selector: "app-audit-home",
  standalone: true,
  imports: [AppTableComponent],
  templateUrl: "./AuditHomePage.html",
  styleUrl: "./AuditHomePage.scss",
})
export class AuditHomePage implements OnInit {
  private readonly auditEffects = inject(AuditEffects);
  private readonly router = inject(Router);
  readonly auditState = inject(AuditState);

  auditColumns: TableColumn<AuditWithSession>[] = [
    { key: "id", header: "ID", sortable: true, width: "10%", visibleByDefault: false },
    {
      key: "session.user.name",
      header: "User",
      sortable: true,
      width: "15%",
      cellTemplate: (item: AuditWithSession) => item.session.user.name,
    },
    {
      key: "session.user.email",
      header: "Email",
      sortable: true,
      width: "20%",
      cellTemplate: (item: AuditWithSession) => item.session.user.email,
    },
    {
      key: "method",
      header: "Method",
      sortable: true,
      width: "10%",
      cellTemplate: (item: AuditWithSession) => item.method,
    },
    {
      key: "url",
      header: "URL",
      sortable: true,
      width: "25%",
      cellTemplate: (item: AuditWithSession) => item.url,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "20%",
      cellTemplate: (item: AuditWithSession) => formatDateNumeric(item.createdAt),
    },
  ];

  get auditConfig(): TableConfig<AuditWithSession> {
    return {
      loading: this.auditState.loading(),
      emptyMessage: "No audits found",
      showActions: true,
      showSearch: true,
      searchPlaceholder: "Search audits...",
      showCreate: false,
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by audit date",
      actionButtons: [
        {
          icon: "open_in_new",
          label: "View Details",
          handler: (item: AuditWithSession) => this.viewAuditDetails(item),
          visible: () => true,
        },
      ],
    };
  }

  get auditPagination(): TablePagination {
    return this.auditState.pagination();
  }

  ngOnInit(): void {
    this.auditEffects.searchAudits().catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    // Removed logging
  }

  onPageChange(event: { page: number; search?: string }): void {
    const currentSearchParams = this.auditState.searchParams();
    this.auditEffects.searchAudits({ ...currentSearchParams, page: event.page, search: event.search }).catch(() => undefined);
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const currentSearchParams = this.auditState.searchParams();
    this.auditEffects
      .searchAudits({ ...currentSearchParams, page: 1, limit: event.limit, search: event.search })
      .catch(() => undefined);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    // Removed logging
  }

  onSearchChange(event: TableSearchEvent): void {
    this.auditEffects.searchAudits({ search: event.term, page: event.page }).catch(() => undefined);
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const currentSearchParams = this.auditState.searchParams();
    this.auditEffects
      .searchAudits({
        ...currentSearchParams,
        page: event.page,
        createdAtFrom: event.dateRange.from,
        createdAtTo: event.dateRange.to,
      })
      .catch(() => undefined);
  }

  viewAuditDetails(audit: AuditWithSession): void {
    this.router.navigate([`/audits/view/${audit.id}`], { state: { audit } }).catch(console.error);
  }
}
